import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { ButtonColor } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import { Modal } from "@/components/Modals/Modal";
import { Radio } from "@/components/Radio/Radio";

import { ContactSelectType } from "../../Contacts/schema/contacts";

export interface BlacklistTableModalsProps {
	ts: (key: string) => string;
	selectedCount: number;
	itemCount: number | undefined;
	limit: number;
	showAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	prepSelectType: ContactSelectType;
	setPrepSelectType: Dispatch<SetStateAction<ContactSelectType>>;
	onConfirmSelectType: () => void;
	showRemoveModal: boolean;
	setShowRemoveModal: Dispatch<SetStateAction<boolean>>;
	removeOrganization: boolean;
	setRemoveOrganization: Dispatch<SetStateAction<boolean>>;
	onRemove: () => Promise<void>;
	isRemoving: boolean;
	showSnoozeModal: boolean;
	setShowSnoozeModal: Dispatch<SetStateAction<boolean>>;
	snoozedUntil: Date;
	setSnoozedUntil: Dispatch<SetStateAction<Date>>;
	onSnooze: () => Promise<void>;
	isSnoozing: boolean;
	showUnsnoozeModal: boolean;
	setShowUnsnoozeModal: Dispatch<SetStateAction<boolean>>;
	onUnsnooze: () => Promise<void>;
	isUnsnoozing: boolean;
}

/** The confirmation dialogs of the Blacklist tab (select-all, remove, snooze, unsnooze). */
export const BlacklistTableModals: FunctionComponent<BlacklistTableModalsProps> = ({
	ts,
	selectedCount,
	itemCount,
	limit,
	showAll,
	setShowAll,
	prepSelectType,
	setPrepSelectType,
	onConfirmSelectType,
	showRemoveModal,
	setShowRemoveModal,
	removeOrganization,
	setRemoveOrganization,
	onRemove,
	isRemoving,
	showSnoozeModal,
	setShowSnoozeModal,
	snoozedUntil,
	setSnoozedUntil,
	onSnooze,
	isSnoozing,
	showUnsnoozeModal,
	setShowUnsnoozeModal,
	onUnsnooze,
	isUnsnoozing,
}) => {
	const { t } = useTranslation();

	return (
		<>
			{showAll && (
				<Modal
					handleClose={() => setShowAll(false)}
					handleSave={async () => onConfirmSelectType()}
					isOpened={showAll}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={ts("select")}
				>
					<div className="flex flex-col items-start gap-4">
						<Radio
							isChecked={prepSelectType === ContactSelectType.ALL}
							onChange={() => setPrepSelectType(ContactSelectType.ALL)}
						>
							<>
								{ts("all")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{itemCount}
								</span>
							</>
						</Radio>
						<Radio
							isChecked={prepSelectType === ContactSelectType.CURRENT_PAGE}
							onChange={() => setPrepSelectType(ContactSelectType.CURRENT_PAGE)}
						>
							<>
								{ts("currentPage")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{itemCount && itemCount < limit ? itemCount : limit}
								</span>
							</>
						</Radio>
					</div>
				</Modal>
			)}

			{showRemoveModal && (
				<Modal
					handleClose={() => {
						setShowRemoveModal(false);
						setRemoveOrganization(false);
					}}
					handleSave={onRemove}
					isLoading={isRemoving}
					isOpened={showRemoveModal}
					size="xs"
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.confirm")}
					title={ts("blacklist.removeTitle")}
				>
					<div className="flex flex-col gap-4">
						<p>{ts("blacklist.removeConfirmation").replace("{{count}}", String(selectedCount))}</p>
						<Checkbox
							isChecked={removeOrganization}
							label={ts("blacklist.removeOrganizationToo")}
							onChange={() => setRemoveOrganization((previous) => !previous)}
						/>
					</div>
				</Modal>
			)}

			{showSnoozeModal && (
				<Modal
					handleClose={() => setShowSnoozeModal(false)}
					handleSave={onSnooze}
					isLoading={isSnoozing}
					isOpened={showSnoozeModal}
					size="xs"
					submitButtonText={ts("blacklist.snoozeAction")}
					title={ts("blacklist.snoozeTitle")}
				>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							{ts("blacklist.snoozeConfirmation").replace("{{count}}", String(selectedCount))}
							<InputField
								handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setSnoozedUntil(new Date(e.target.value))
								}
								name="snoozedUntil"
								placeholder={ts("blacklist.snoozedUntil")}
								type="datetime-local"
								value={snoozedUntil.toISOString().substring(0, 19)}
							/>
						</div>
					</div>
				</Modal>
			)}

			{showUnsnoozeModal && (
				<Modal
					handleClose={() => setShowUnsnoozeModal(false)}
					handleSave={onUnsnooze}
					isLoading={isUnsnoozing}
					isOpened={showUnsnoozeModal}
					size="xs"
					submitButtonText={ts("blacklist.unsnoozeAction")}
					title={ts("blacklist.unsnoozeTitle")}
				>
					<div className="flex flex-col gap-2">
						{ts("blacklist.unsnoozeConfirmation").replace("{{count}}", String(selectedCount))}
					</div>
				</Modal>
			)}
		</>
	);
};
